export interface AnimationConfig {
  threshold: number;
  dragSpeedMultiplier: number;
  friction: number;
  minVelocity: number;
  minDragInertia: number;
}

export interface ProgressState {
  current: number;
  target: number;
}

export class MpsAnimationController {
  private animationId?: number;
  private onProgressUpdate: (progress: number) => void;

  constructor(
    private config: AnimationConfig,
    onProgressUpdate: (progress: number) => void,
  ) {
    this.onProgressUpdate = onProgressUpdate;
  }

  // ===========================================
  // 애니메이션 제어
  // ===========================================

  startAnimation(progressState: ProgressState, animationSpeed: number, onComplete?: () => void): void {
    this.stop();
    this.animate(progressState, animationSpeed, onComplete);
  }

  startDragAnimation(progressState: ProgressState, animationSpeed: number, onComplete?: () => void): void {
    if (this.animationId) return; // 이미 실행 중이면 목표값만 업데이트

    const dragAnimationSpeed = Math.min(1, animationSpeed * this.config.dragSpeedMultiplier);
    this.animate(progressState, dragAnimationSpeed, onComplete);
  }

  startInertia(
    progressState: ProgressState,
    velocity: number,
    containerWidth: number,
    onComplete?: () => void,
  ): void {
    this.stop();

    // 드래그에서 보장된 최소 속도는 항상 실행
    const isMinDragVelocity = Math.abs(velocity) === this.config.minDragInertia;
    if (!isMinDragVelocity && Math.abs(velocity) < this.config.minVelocity) {
      this.startSmoothToBounds(progressState, onComplete);
      return;
    }

    let currentVelocity = -(velocity / containerWidth);

    const step = () => {
      progressState.current += currentVelocity;

      // 최소 드래그 속도로 시작된 경우 마찰력 조정
      const friction = isMinDragVelocity ? this.config.friction + 0.02 : this.config.friction;

      currentVelocity *= friction;

      // 경계값 체크
      if (progressState.current < 0) {
        progressState.current = 0;
        currentVelocity = 0; // boundaryReflection 제거 (즉시 정지)
      } else if (progressState.current > 1) {
        progressState.current = 1;
        currentVelocity = 0;
      }

      this.onProgressUpdate(progressState.current);

      // 정지 조건 확인
      const stopThreshold = isMinDragVelocity ? this.config.minVelocity * 0.5 : this.config.minVelocity;

      if (Math.abs(currentVelocity) < stopThreshold) {
        this.startSmoothToBounds(progressState, onComplete);
        return;
      }

      this.animationId = requestAnimationFrame(step);
    };

    step();
  }

  startSmoothToBounds(progressState: ProgressState, onComplete?: () => void): void {
    const boundedProgress = Math.max(0, Math.min(1, progressState.current));
    progressState.target = boundedProgress;
    this.startAnimation(progressState, 0.1, onComplete); // 기본 animationSpeed 사용
  }

  stop(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = undefined;
    }
  }

  isRunning(): boolean {
    return this.animationId !== undefined;
  }

  // ===========================================
  // 내부 메서드
  // ===========================================

  private animate(progressState: ProgressState, animationSpeed: number, onComplete?: () => void): void {
    const step = () => {
      const diff = progressState.target - progressState.current;

      if (Math.abs(diff) > this.config.threshold) {
        progressState.current += diff * animationSpeed;
        this.onProgressUpdate(progressState.current);
        this.animationId = requestAnimationFrame(step);
      } else {
        progressState.current = progressState.target;
        this.onProgressUpdate(progressState.current);
        this.animationId = undefined;
        onComplete?.();
      }
    };

    step();
  }

  // ===========================================
  // 설정 업데이트
  // ===========================================

  updateConfig(newConfig: Partial<AnimationConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  getConfig(): AnimationConfig {
    return { ...this.config };
  }
}
